// External component imports
import { Typography } from '@interstate/components/Typography';
import { useInterstateTheme } from '@interstate/components/InterstateThemeProvider';
import { Grid } from '@interstate/components/Grid';

// Constants and types
import type { ComplianceSections } from '../../shared/types';
import { ComplianceStatus } from '../../shared/types';
import { COMPLIANCE, COMPLIANCE_RED_FLAG_MESSAGE } from '../../shared/constants';

// Local style imports
import { ContainerBox, useStyles } from '../ComplianceAccordian.style';
import { Separator } from '../../ComplianceForm.style';
import { useSelector } from 'react-redux';
import * as complianceSelectors from '@makemydeal/dr-dash-store/selectors/complianceSelectors';
import { IconByLevel } from './IconByLevel';
import { Alert } from '@interstate/components/Alert';

export const RedFlagsDetails = () => {
    const theme = useInterstateTheme();
    const classes = useStyles();

    const participantMatch = useSelector(complianceSelectors.getComplianceParticipantForShopper);
    const data = (participantMatch?.compliance ?? {}) as ComplianceSections;
    const redFlagData = data.redFlags ?? {};

    const status = redFlagData.status as ComplianceStatus;
    const findings = Array.isArray(redFlagData.findings) ? redFlagData.findings : [];
    const message = COMPLIANCE_RED_FLAG_MESSAGE.MESSAGES[status as keyof typeof COMPLIANCE_RED_FLAG_MESSAGE.MESSAGES] || '';

    // Handle ERROR
    if (status === ComplianceStatus.ERROR) {
        return (
            <>
                <Alert data-testid="error-alert" title={COMPLIANCE.ERROR_ALERT.TITLE} type="error">
                    <span>{COMPLIANCE.ERROR_ALERT.DESCRIPTION}</span>
                </Alert>
                <Separator
                    isVisible={true}
                    theme={theme}
                    className="card_separator"
                    data-testid="card_separator"
                    aria-hidden="true"
                />
            </>
        );
    }

    // Handle FAIL
    if (status === ComplianceStatus.FAIL) {
        return (
            <ContainerBox>
                <Typography variant="body-sm">{message.details}</Typography>
                <Separator
                    isVisible={false}
                    theme={theme}
                    className="card_separator"
                    data-testid="card_hidden_separator"
                    aria-hidden="true"
                />
                {findings.map((finding) => (
                    <Grid
                        key={`finding-${finding.name}`}
                        className={classes.container}
                        data-testid={`red-flag-detail-${finding.name}`}
                    >
                        <Grid xs="auto" className={classes.iconWrapper}>
                            <IconByLevel level={finding.level} />
                        </Grid>
                        <Grid xs="auto" className={classes.textWrapper}>
                            <Typography variant="body-sm">{finding.value}</Typography>
                        </Grid>
                    </Grid>
                ))}
            </ContainerBox>
        );
    }

    // Handle PASS
    if (status === ComplianceStatus.PASS) {
        return (
            <ContainerBox>
                <Typography variant="strong-sm">{message.title}</Typography>
                <Typography variant="body-sm">{message.description}</Typography>
            </ContainerBox>
        );
    }

    // Default case for STARTED, NOT_STARTED, NOT_APPLICABLE, COMPLETED
    return (
        <ContainerBox>
            <Typography variant="body-sm">{message.details}</Typography>
        </ContainerBox>
    );
};
